'use client';

/**
 * PipelineBuilder Component
 *
 * Drag-and-drop stage configuration for building pipelines.
 * Allows adding, removing, and reordering stages with live preview.
 */

import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  type UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Box from '@mui/material/Box';
import { styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useMemo, useState } from 'react';
import type { NeonAccent } from '../../theme/palette';
import { getNeonStyles, neonAccents } from '../../theme/palette';
import { glassEffects, neonGlow } from '../../theme/shadows';
import { transitionPresets } from '../../theme/transitions';
import type { Pipeline, PipelineStage } from '../../types/pipeline';
import { GlassButton } from '../GlassButton';
import { GlassIconButton } from '../GlassIconButton';
import { StageEditor } from './StageEditor';

// ============================================================================
// Types
// ============================================================================

export interface PipelineBuilderProps {
  /** Existing pipeline to edit */
  pipeline?: Pipeline;
  /** Change handler */
  onChange: (pipeline: Pipeline) => void;
  /** Whether the builder is disabled */
  disabled?: boolean;
  /** Neon accent color */
  neonAccent?: NeonAccent;
  /** Additional class name */
  className?: string;
}

// ============================================================================
// Styled Components
// ============================================================================

interface StyledContainerProps {
  $isDark: boolean;
}

const StyledContainer = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledContainerProps>(({ $isDark }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;

  return {
    padding: 16,
    borderRadius: 16,
    background: glass.background,
    border: `1px solid ${glass.border}`,
    backdropFilter: glass.backdropBlur,
  };
});

const Header = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
}));

const StageList = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  minHeight: 200,
}));

interface StageItemContainerProps {
  $isDark: boolean;
  $neonAccent: NeonAccent;
  $isDragging: boolean;
  $isDisabled: boolean;
}

const StageItemContainer = styled(motion.div, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StageItemContainerProps>(({ $isDark, $neonAccent, $isDragging, $isDisabled }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;
  const neonStyles = getNeonStyles($neonAccent, $isDark);
  const glow = neonGlow[$neonAccent];

  return {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 16px',
    borderRadius: 12,
    background: glass.background,
    border: `1px solid ${$isDragging ? neonStyles.border : glass.border}`,
    boxShadow: $isDragging ? glow.medium : 'none',
    opacity: $isDisabled ? 0.5 : 1,
    cursor: $isDisabled ? 'not-allowed' : 'grab',
    transition: transitionPresets.card,
    touchAction: 'none',

    '&:hover': $isDisabled
      ? {}
      : {
          background: glass.backgroundHover,
          border: `1px solid ${glass.borderHover}`,
        },
  };
});

const DragHandle = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $isDark: boolean }>(({ $isDark }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
  cursor: 'grab',
  color: $isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
  flexShrink: 0,

  '&:active': {
    cursor: 'grabbing',
  },

  '& svg': {
    width: 16,
    height: 16,
  },
}));

const StageColorDot = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $color: NeonAccent }>(({ $color }) => ({
  width: 10,
  height: 10,
  borderRadius: '50%',
  background: neonAccents[$color].base,
  boxShadow: `0 0 8px ${neonAccents[$color].glow}`,
  flexShrink: 0,
}));

const StageInfo = styled(Box)({
  flex: 1,
  minWidth: 0,
});

const StageActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  flexShrink: 0,
}));

const EmptyState = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $isDark: boolean }>(({ theme, $isDark }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  textAlign: 'center',
  border: `2px dashed ${$isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
  borderRadius: 12,
}));

const OrderNumber = styled(Typography, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $neonAccent: NeonAccent }>(({ $neonAccent }) => ({
  fontSize: '0.75rem',
  fontWeight: 700,
  color: neonAccents[$neonAccent].base,
  opacity: 0.7,
  minWidth: 16,
  textAlign: 'center',
}));

// ============================================================================
// Icons
// ============================================================================

const DragHandleIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <circle cx="9" cy="6" r="1.5" />
    <circle cx="15" cy="6" r="1.5" />
    <circle cx="9" cy="12" r="1.5" />
    <circle cx="15" cy="12" r="1.5" />
    <circle cx="9" cy="18" r="1.5" />
    <circle cx="15" cy="18" r="1.5" />
  </svg>
);

const EditIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
  </svg>
);

const DeleteIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const AddIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

// ============================================================================
// Sortable Stage Item Component
// ============================================================================

interface SortableStageItemProps {
  stage: PipelineStage;
  index: number;
  disabled: boolean;
  neonAccent: NeonAccent;
  onEdit: (stage: PipelineStage) => void;
  onDelete: (stageId: string) => void;
}

function SortableStageItem({ stage, index, disabled, neonAccent, onEdit, onDelete }: SortableStageItemProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: stage.id,
    disabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1000 : undefined,
  };

  return (
    <StageItemContainer
      ref={setNodeRef}
      style={style}
      $isDark={isDark}
      $neonAccent={stage.color || neonAccent}
      $isDragging={isDragging}
      $isDisabled={disabled}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.2 }}
    >
      {/* Drag Handle */}
      <DragHandle $isDark={isDark} {...attributes} {...listeners}>
        <DragHandleIcon />
      </DragHandle>
      {/* Order Number */}
      <OrderNumber $neonAccent={stage.color || neonAccent}>{index + 1}</OrderNumber>
      {/* Color Dot */}
      <StageColorDot $color={stage.color || neonAccent} />
      {/* Stage Info */}
      <StageInfo>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {stage.name || 'Untitled Stage'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, mt: 0.25 }}>
          {stage.isFinal && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              Final
              {stage.isSuccess && ' (Success)'}
              {stage.isFailure && ' (Failure)'}
            </Typography>
          )}
          {stage.sla && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              SLA: {stage.sla.targetHours}h
            </Typography>
          )}
          {stage.wipLimit && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              WIP: {stage.wipLimit}
            </Typography>
          )}
        </Box>
      </StageInfo>
      {/* Actions */}
      <StageActions>
        <GlassIconButton size="small" onClick={() => onEdit(stage)} disabled={disabled} title="Edit stage">
          <EditIcon />
        </GlassIconButton>
        <GlassIconButton
          size="small"
          color="error"
          onClick={() => onDelete(stage.id)}
          disabled={disabled}
          title="Delete stage"
        >
          <DeleteIcon />
        </GlassIconButton>
      </StageActions>
    </StageItemContainer>
  );
}

// ============================================================================
// Helper Functions
// ============================================================================

const generateId = (): string => {
  return `stage_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

const createDefaultPipeline = (): Pipeline => ({
  id: `pipeline_${Date.now()}`,
  name: 'New Pipeline',
  description: '',
  entityId: '',
  stages: [],
  defaultStageId: '',
  isActive: true,
  createdAt: new Date(),
  updatedAt: new Date(),
});

// ============================================================================
// Component
// ============================================================================

export function PipelineBuilder({
  pipeline: initialPipeline,
  onChange,
  disabled = false,
  neonAccent = 'cyan',
  className,
}: PipelineBuilderProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Internal pipeline state
  const [pipeline, setPipeline] = useState<Pipeline>(initialPipeline || createDefaultPipeline());

  // Editor modal state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingStage, setEditingStage] = useState<PipelineStage | null>(null);

  // Drag state
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);

  // Configure sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Sorted stages
  const sortedStages = useMemo(() => {
    return [...pipeline.stages].sort((a, b) => a.order - b.order);
  }, [pipeline.stages]);

  // Stage IDs for sortable context
  const stageIds = useMemo(() => sortedStages.map((s) => s.id), [sortedStages]);

  // Find active stage for drag overlay
  const activeStage = useMemo(() => {
    if (!activeId) return null;
    return sortedStages.find((s) => s.id === activeId) || null;
  }, [activeId, sortedStages]);

  // Update pipeline and notify parent
  const updatePipeline = useCallback(
    (updates: Partial<Pipeline>) => {
      const updated = {
        ...pipeline,
        ...updates,
        updatedAt: new Date(),
      };
      setPipeline(updated);
      onChange(updated);
    },
    [pipeline, onChange],
  );

  // Handle drag start
  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id);
  }, []);

  // Handle drag end
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveId(null);

      if (!over || active.id === over.id) return;

      const oldIndex = sortedStages.findIndex((s) => s.id === active.id);
      const newIndex = sortedStages.findIndex((s) => s.id === over.id);

      if (oldIndex === -1 || newIndex === -1) return;

      const newStages = arrayMove(sortedStages, oldIndex, newIndex).map((stage, index) => ({
        ...stage,
        order: index,
      }));

      updatePipeline({ stages: newStages });
    },
    [sortedStages, updatePipeline],
  );

  // Handle add stage
  const handleAddStage = useCallback(() => {
    const newStage: PipelineStage = {
      id: generateId(),
      name: '',
      order: sortedStages.length,
      color: 'cyan',
      allowedTransitions: [],
      isFinal: false,
    };
    setEditingStage(newStage);
    setEditorOpen(true);
  }, [sortedStages.length]);

  // Handle edit stage
  const handleEditStage = useCallback((stage: PipelineStage) => {
    setEditingStage(stage);
    setEditorOpen(true);
  }, []);

  // Handle save stage
  const handleSaveStage = useCallback(
    (stage: PipelineStage) => {
      const existingIndex = pipeline.stages.findIndex((s) => s.id === stage.id);

      let newStages: PipelineStage[];
      if (existingIndex >= 0) {
        // Update existing stage
        newStages = pipeline.stages.map((s) => (s.id === stage.id ? stage : s));
      } else {
        // Add new stage
        newStages = [...pipeline.stages, stage];
      }

      // Ensure orders are sequential
      newStages = newStages.sort((a, b) => a.order - b.order).map((s, i) => ({ ...s, order: i }));

      // Set default stage if this is the first stage
      const defaultStageId = pipeline.defaultStageId || (newStages.length > 0 ? newStages[0].id : '');

      updatePipeline({ stages: newStages, defaultStageId });
      setEditorOpen(false);
      setEditingStage(null);
    },
    [pipeline, updatePipeline],
  );

  // Handle delete stage
  const handleDeleteStage = useCallback(
    (stageId: string) => {
      const newStages = pipeline.stages.filter((s) => s.id !== stageId).map((s, i) => ({ ...s, order: i }));

      // Update default stage if needed
      let defaultStageId = pipeline.defaultStageId;
      if (defaultStageId === stageId) {
        defaultStageId = newStages.length > 0 ? newStages[0].id : '';
      }

      updatePipeline({ stages: newStages, defaultStageId });
      setEditorOpen(false);
      setEditingStage(null);
    },
    [pipeline, updatePipeline],
  );

  // Handle close editor
  const handleCloseEditor = useCallback(() => {
    setEditorOpen(false);
    setEditingStage(null);
  }, []);

  return (
    <StyledContainer $isDark={isDark} className={className}>
      {/* Header */}
      <Header>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
          }}
        >
          {pipeline.name}
        </Typography>
        <GlassButton
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddStage}
          disabled={disabled}
        >
          Add Stage
        </GlassButton>
      </Header>
      {/* Stage List */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={stageIds} strategy={verticalListSortingStrategy}>
          <StageList>
            <AnimatePresence mode="popLayout">
              {sortedStages.length === 0 ? (
                <EmptyState $isDark={isDark}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      mb: 1,
                    }}
                  >
                    No stages yet
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.disabled',
                    }}
                  >
                    Click "Add Stage" to create your first pipeline stage
                  </Typography>
                </EmptyState>
              ) : (
                sortedStages.map((stage, index) => (
                  <SortableStageItem
                    key={stage.id}
                    stage={stage}
                    index={index}
                    disabled={disabled}
                    neonAccent={neonAccent}
                    onEdit={handleEditStage}
                    onDelete={handleDeleteStage}
                  />
                ))
              )}
            </AnimatePresence>
          </StageList>
        </SortableContext>

        {/* Drag Overlay */}
        <DragOverlay dropAnimation={null}>
          {activeStage && (
            <StageItemContainer
              $isDark={isDark}
              $neonAccent={activeStage.color || neonAccent}
              $isDragging={true}
              $isDisabled={false}
              style={{ cursor: 'grabbing' }}
            >
              <DragHandle $isDark={isDark}>
                <DragHandleIcon />
              </DragHandle>
              <OrderNumber $neonAccent={activeStage.color || neonAccent}>
                {sortedStages.findIndex((s) => s.id === activeStage.id) + 1}
              </OrderNumber>
              <StageColorDot $color={activeStage.color || neonAccent} />
              <StageInfo>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  {activeStage.name || 'Untitled Stage'}
                </Typography>
              </StageInfo>
            </StageItemContainer>
          )}
        </DragOverlay>
      </DndContext>
      {/* Stage Editor Modal */}
      <StageEditor
        stage={editingStage}
        open={editorOpen}
        onClose={handleCloseEditor}
        onSave={handleSaveStage}
        onDelete={handleDeleteStage}
        isFirstStage={editingStage ? sortedStages[0]?.id === editingStage.id : false}
        isLastStage={editingStage ? sortedStages[sortedStages.length - 1]?.id === editingStage.id : false}
        neonAccent={neonAccent}
      />
    </StyledContainer>
  );
}

PipelineBuilder.displayName = 'PipelineBuilder';

export default PipelineBuilder;
